/* eslint-disable react-hooks/exhaustive-deps */
/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-06-18 11:39:10
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { withI18n } from '@shopify/react-i18n';
import {
  Page,
  Layout,
  Card,
  SkeletonBodyText,
  ResourceList,
  ResourceItem,
  Link,
  Icon
} from '@shopify/polaris';
import { LockMinor, ExternalSmallMinor } from '@shopify/polaris-icons';

import MainMenu from 'src/components/containers/main-menu';
import InstallationRemindStatusBanner from 'src/components/containers/common/installation-remind-status-banner';
import FiltersBar from 'src/components/views/recommendation/predictive-bundle/filters-bar';
import ActionGroup from 'src/components/views/recommendation/predictive-bundle/action-group';
import ListWidgets from 'src/components/views/recommendation/predictive-bundle/list-widgets';

import NoWidgetFound from 'src/components/views/recommendation/predictive-bundle/list-widgets/no-widget-found';
import GrantPermissionsNotify from 'src/components/views/recommendation/predictive-bundle/grant-permissions-notify';
import DidYouKnow from 'src/components/views/recommendation/predictive-bundle/did-you-know';
import TitleHelp from 'src/components/views/common/title-help';
import YoutubeModal from 'src/components/views/common/youtube-modal';
import WarningUpgradeTurboModal from 'src/components/views/recommendation/predictive-bundle/warning-upgrade-theme-turbo';

import { URL } from 'src/constants/links';
import history from 'src/routes/history';
import { getShop, buildUpdateAppPermissionURL } from 'src/helpers';
import { hasPermissionBundleDiscount } from 'src/helpers/permission-helper';
import { routes } from 'src/routes';
import * as bundleActions from 'src/stores/actions/bundle-actions';
import { checkUserEnableShowAppVersionSwitching } from 'src/helpers/account-helper';
import { enableShopifyIntegration } from 'src/helpers/theme-setup-helper';

import LockImage from 'src/assets/images/lock-unlock-image/predictive-bundle-lock.png';

import type { ComposedProps } from './index.type';

const DISMISS_POPUP_KEY = 'isDismissWarningTurboBundlePopup';

const Bundles = (props: ComposedProps) => {
  const {
    i18n,
    list,
    enabledFeature,
    syncWidgets,
    accessScopes,
    loading,
    success,
    getPredictiveBundleList,
    enableAppVersionSwitching,
    isEnableShopifyIntegration,
    updateWidget,
    deleteWidget
  } = props;
  const [allWidgets, setAllWidgets] = useState([]);
  const [permissionURL, setPermissionURL] = useState('');
  const [showPermissionBanner, setShowPermissionBanner] = useState(false);
  const [isSearch, setIsSearch] = useState(false);
  const [query, setQuery] = useState('');
  const [youtubeModal, setYoutubeModal] = useState(false);
  const [syncWidgetsFlag, setSyncWidgetsFlag] = useState(0);
  const [warningTurboModal, setWarningTurboModal] = useState(false);

  let timerTyping = null;

  // Show notification
  const showGrantPermissionsBanner = () => {
    // Remove localStorage
    try {
      const keyRedirect = `${getShop()}-permissionApproval-redirectURI`;
      const valueRedirect = localStorage.getItem(keyRedirect);
      if (valueRedirect === '/predictive-bundle') {
        localStorage.removeItem(keyRedirect);
      }
    } catch (error) {}

    if (hasPermissionBundleDiscount(accessScopes) || !isEnableShopifyIntegration) return;
    // Get update permission link
    const _permissionURL = buildUpdateAppPermissionURL(getShop());

    setShowPermissionBanner(true);
    setPermissionURL(_permissionURL);
  };

  // Push widgets to list every time after reading from DB
  const pushRecommendationWidgetsToList = (filteredWidgets = []) => {
    let widgets = [];
    // Merge new widgets
    if (isSearch) {
      widgets = filteredWidgets;
    } else {
      widgets = list;
    }
    setAllWidgets(widgets);
  };

  // Handle refresh widgets action
  const handleSyncWidgets = () => {
    const meta = {
      onSuccess: () => {
        // Reset filter and search
        setQuery('');
        setIsSearch(false);
        setSyncWidgetsFlag(1 - syncWidgetsFlag);
        // Update widget list with the new ones
        pushRecommendationWidgetsToList();
      }
    };
    syncWidgets(meta);
  };

  // Key press event on search field
  const handleKeyDown = event => {
    clearTimeout(timerTyping);
  };

  // search widgets by query value
  const searchWidget = widgets => {
    let queryValue = query.toLowerCase().trim();
    let filteredWidgets = widgets;
    // Having search text, search widgets
    if (queryValue) {
      // Enable flag search
      setIsSearch(true);
      filteredWidgets = widgets.filter(widget => {
        const widgetName = _.get(widget, 'widgetName', '');
        const widgetId = _.get(widget, 'id', '');
        return (
          widgetName.toLowerCase().includes(queryValue) ||
          widgetId.toLowerCase().includes(queryValue)
        );
      });
    } else {
      setIsSearch(false);
    }
    return filteredWidgets;
  };

  // Handle search
  const handleFilterAndSearch = async () => {
    // Apply search
    const filteredWidgets = searchWidget(list);
    pushRecommendationWidgetsToList(filteredWidgets);
  };

  // Key up event, start searching after 500ms
  const handleKeyUp = event => {
    // Invoke search request after 500ms
    timerTyping = setTimeout(() => {
      handleFilterAndSearch();
    }, 500);
  };

  // Handle update widget
  const handleUpdateWidget = (id, data) => {
    updateWidget(id, data);
  };
  const handleDeleteWidget = id => {
    deleteWidget(id);
  };

  const handleAddNewOffer = () => {
    try {
      const doNotShowTurboModalAgain = localStorage.getItem(`${getShop()}-${DISMISS_POPUP_KEY}`);
      // When show popup warning upgrade todo
      // - store never check: don't show again (use local storage)
      // - is old store that using both v3 version and turbo (enableAppVersionSwitching = true)
      // - already upgrade turbo version (isEnableShopifyIntegration = true)
      if (!doNotShowTurboModalAgain && enableAppVersionSwitching && isEnableShopifyIntegration) {
        setWarningTurboModal(true);
        return;
      }
    } catch {}
    history.push(routes.predictiveBundle.new());
  };

  const handleGotItWarningTurboModal = doNotShowAgain => {
    if (doNotShowAgain) {
      try {
        localStorage.setItem(`${getShop()}-${DISMISS_POPUP_KEY}`, doNotShowAgain);
      } catch (err) {}
    }
    history.push(routes.predictiveBundle.new());
  };

  useEffect(() => {
    // Show Grant permission notification
    showGrantPermissionsBanner();
    if (enabledFeature) {
      getPredictiveBundleList();
    }
  }, [enabledFeature]);

  useEffect(() => {
    if (success) {
      setAllWidgets(list);
    }
  }, [success, list]);

  // render lock card for case freemium plan
  const renderLockCard = (i18n, isEnableShopifyIntegration) => {
    return (
      <div className="pfs-feature-lock-card">
        <img src={LockImage} alt="in-collection-search-lock" />
        <div className="content">
          {i18n.translate(`AdminHTML.PredictiveBundle.Plan.lockCardContent01`)}
        </div>
        <p>{i18n.translate(`AdminHTML.PredictiveBundle.Plan.lockCardContent02`)}</p>
        <div className="action">
          {!isEnableShopifyIntegration ? (
            <div
              className={`Polaris-Button`}
              onClick={() => history.push(routes.appPreferences.view())}>
              <Icon source={LockMinor} />
              {i18n.translate(`AdminHTML.PredictiveBundle.Plan.lockCardAction`)}
            </div>
          ) : (
            <div
              className={`Polaris-Button`}
              onClick={() => history.push(routes.subscription.package())}>
              <Icon source={LockMinor} />
              {i18n.translate(`AdminHTML.Common.Action.upgradeToUnlock`)}
            </div>
          )}
          <Link
            external
            url={
              URL[
                `${
                  !isEnableShopifyIntegration
                    ? 'HowToUpgradeTurbo'
                    : 'Link_V2_Recommendation_PredictiveBundle_ManualGuide'
                }`
              ]
            }>
            {i18n.translate('AdminHTML.Common.Action.learnMore')}
            <Icon source={ExternalSmallMinor} color={'interactive'}></Icon>
          </Link>
        </div>
      </div>
    );
  };

  return (
    <div className="BC-Page PFS-Page__PredictiveBundle-Management">
      <MainMenu menuId="menu-predictive-bundle" />
      <Page
        title={i18n.translate('AdminHTML.PredictiveBundle.name')}
        primaryAction={
          <div className="BC-Page BC-Page__Button">
            <ActionGroup handleAddNewOffer={handleAddNewOffer} />
          </div>
        }>
        <TitleHelp
          i18n={i18n}
          // onPlay={() => setYoutubeModal(true)}
          hrefUserManual={URL['Link_V2_Recommendation_PredictiveBundle_ManualGuide']}
        />
        <Layout>
          <Layout.Section>
            <div className="BC-Container__Notifications">
              {/* Installation reminder status banner */}
              <InstallationRemindStatusBanner />

              <DidYouKnow />

              {/* Update permission recommendation*/}
              {showPermissionBanner && <GrantPermissionsNotify permissionURL={permissionURL} />}
            </div>
          </Layout.Section>

          {/* List of widgets */}
          <Layout.Section>
            <div className="BC-Management-Widget">
              <Card>
                <FiltersBar
                  queryValue={query}
                  onQueryChange={query => setQuery(query)}
                  handleKeyDown={handleKeyDown}
                  handleKeyUp={handleKeyUp}
                  syncWidgets={handleSyncWidgets}
                />
                {enabledFeature && isEnableShopifyIntegration ? (
                  loading ? (
                    <ResourceList
                      resourceName={{ singular: 'widget', plural: 'widgets' }}
                      items={[...Array(3).keys()]}
                      renderItem={item => {
                        return (
                          <ResourceItem id={item}>
                            <SkeletonBodyText lines={2} />
                          </ResourceItem>
                        );
                      }}
                    />
                  ) : allWidgets.length === 0 ? (
                    /* Incase not widget found when search */
                    <NoWidgetFound isSearch={isSearch} />
                  ) : (
                    <ListWidgets
                      allWidgets={allWidgets}
                      updateWidget={handleUpdateWidget}
                      handleDeleteWidget={handleDeleteWidget}
                    />
                  )
                ) : (
                  renderLockCard(i18n, isEnableShopifyIntegration)
                )}
              </Card>
            </div>
          </Layout.Section>
        </Layout>
        {allWidgets.length > 0 && (
          <div className="Polaris-PageActions">
            <div className="Polaris-Stack Polaris-Stack--spacingTight Polaris-Stack--distributionTrailing">
              <ActionGroup handleAddNewOffer={handleAddNewOffer} />
            </div>
          </div>
        )}
      </Page>
      <YoutubeModal
        active={youtubeModal}
        embeddedURL={URL['Link_V2_Recommendation_ManageWidget_Video']}
        onClose={() => setYoutubeModal(false)}
        title={i18n.translate('AdminHTML.Analytics.Analytics.youtubeModal_Title')}
      />
      <WarningUpgradeTurboModal
        active={warningTurboModal}
        handleCancel={() => setWarningTurboModal(false)}
        handleConfirm={handleGotItWarningTurboModal}
      />
    </div>
  );
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  // Get recommendation widgets
  getPredictiveBundleList: () => dispatch(bundleActions.getPredictiveBundleList(ownProps.i18n)),
  deleteWidget: widgetId => dispatch(bundleActions.deleteWidget(widgetId, ownProps.i18n)),
  syncWidgets: meta => dispatch(bundleActions.syncWidgets(meta, ownProps.i18n)),
  updateWidget: (widgetId, data, meta) =>
    dispatch(bundleActions.updateWidget(widgetId, data, meta, ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  // Get widgets in case of editing
  // const list = _.get(state.bundle, 'list', []);

  // Get access scopes
  const accessScopes = _.get(state.oauth, 'tenantApp.accessScopes', {});
  const settings = _.get(state.oauth, 'tenantApp.settings', {});
  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  const tenantId = _.get(state.oauth, 'tenantApp.tenantId', '');
  const selectedPlan = _.get(state.oauth.tenantApp, 'selectedPlan', '');
  const enableAppVersionSwitching = checkUserEnableShowAppVersionSwitching(tenantApp);
  const isEnableShopifyIntegration = enableShopifyIntegration(tenantApp);
  return {
    enabledFeature: _.get(
      state.oauth.tenantApp,
      'settings.recommendation.settings.dynamic-bundle',
      !selectedPlan.includes('freemium')
    ),
    list: _.get(state.bundle, 'list', []),
    saving: _.get(state.bundle, 'saving', false),
    deleting: _.get(state.bundle, 'deleting', false),
    success: _.get(state.bundle, 'success', false),
    loading: _.get(state.bundle, 'loading', false),
    widgetsSyncing: _.get(state.bundle, 'widgetsSyncing', false),
    widgetId: _.get(state.bundle, 'widgetId', ''),
    accessScopes,
    enableAppVersionSwitching,
    isEnableShopifyIntegration,
    settings,
    tenantId
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(Bundles));
